import { RefObject, useEffect } from 'react'
import { ChartAPI } from 'c3'

/*
 * Keeps a c3 chart sized to its container as the container changes width, one redraw per animation
 * frame and without c3's redraw transition. Charts using this pass `resize: { auto: false }` so c3
 * doesn't also redraw (animated, after a delay) on window resize events: that delayed, animated
 * redraw is what made charts jump about while the brief's layout was animating.
 *
 * A chart with a set height passes it as `height`, and keeps it. Reading the height back from the page
 * instead let it ratchet down: if the layout squeezed the chart for a moment (the brief resizing), the
 * smaller height was drawn, read back as the new height on the next redraw, and never grew again.
 */
export const useChartResize = (
  elementRef: RefObject<HTMLElement>,
  chartRef: RefObject<ChartAPI | null>,
  enabled = true,
  height?: number
): void => {
  useEffect(() => {
    const el = elementRef.current
    if (!enabled || !el || typeof ResizeObserver === 'undefined') return undefined

    let frame = 0
    let lastWidth = el.clientWidth
    const observer = new ResizeObserver(() => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        const width = el.clientWidth
        if (width === lastWidth || width === 0) return
        lastWidth = width
        chartRef.current?.resize({ width, height: height ?? el.clientHeight })
      })
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [elementRef, chartRef, enabled, height])
}
